import { toast } from "sonner";
import { useState, useContext, useEffect, useMemo } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";

type SimiCatalogItem = {
  id: number;
  value: string;
};

type SimiCatalogs = {
  medio: SimiCatalogItem[];
  asesores: SimiCatalogItem[];
  estado: SimiCatalogItem[];
};

type PortalEntry = {
  key: string;
  label: string;
  required: boolean;
};

type SimiGlobalVariablesConfigurationProps = {
  crmConnection: any;
};

const NON_PORTAL_SOURCES = ["pauta digital"];

const SimiGlobalVariablesConfiguration = ({
  crmConnection,
}: SimiGlobalVariablesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const { data: merchant } = trpc.merchants.get.useQuery(
    { id: merchantId || "" },
    { enabled: !!merchantId }
  );

  const portalEntries = useMemo<PortalEntry[]>(() => {
    const entries: PortalEntry[] = [];
    const portalSchemas = (merchant as any)?.portalSchemas as Array<{ source: string }> | undefined;
    if (portalSchemas) {
      for (const schema of portalSchemas) {
        entries.push({
          key: schema.source,
          label: schema.source,
          required: false,
        });
      }
    }
    for (const source of NON_PORTAL_SOURCES) {
      if (entries.some((entry) => entry.key.toLowerCase() === source)) continue;
      entries.push({ key: source, label: source, required: false });
    }
    entries.push({ key: "default", label: "Default", required: true });
    return entries;
  }, [merchant]);

  const [medioSources, setMedioSources] = useState<Record<string, number>>({});
  const [selectedAsesorId, setSelectedAsesorId] = useState<number | undefined>(
    crmConnection?.simi?.asesorId
  );
  const [catalogs, setCatalogs] = useState<SimiCatalogs | null>(null);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const utils = trpc.useUtils();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSimiCatalogs = (trpc.crmSelfConfig as any).getSimiCatalogs.useMutation({
    onSuccess: (result: any) => {
      if (result.success && result.data) {
        setCatalogs(result.data);
        setIsLoadingCatalogs(false);
      } else if (!result.success) {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los catalogos de Simi.",
        });
        setIsLoadingCatalogs(false);
      }
    },
    onError: (error: any) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al obtener los catalogos de Simi.",
      });
      setIsLoadingCatalogs(false);
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateSimiConnection = (trpc.crmSelfConfig as any).updateSimi.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuracion guardada", {
        description: "Los valores de Simi se han guardado correctamente.",
      });
      setIsSaving(false);
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al guardar la configuracion.",
      });
      setIsSaving(false);
    },
  });

  const fetchCatalogs = () => {
    if (
      crmConnection?.simi?.clientId &&
      crmConnection?.simi?.clientSecret &&
      crmConnection?.simi?.scope &&
      crmConnection?.simi?.inmobiliaria
    ) {
      setIsLoadingCatalogs(true);
      getSimiCatalogs.mutate({
        clientId: crmConnection.simi.clientId,
        clientSecret: crmConnection.simi.clientSecret,
        scope: crmConnection.simi.scope,
        inmobiliaria: crmConnection.simi.inmobiliaria,
        accessToken: crmConnection.simi.accessToken,
      });
    }
  };

  useEffect(() => {
    if (!catalogs && !isLoadingCatalogs) {
      fetchCatalogs();
    }
  }, [crmConnection?.simi?.clientId]);

  useEffect(() => {
    if (isEditing && !catalogs && !isLoadingCatalogs) {
      fetchCatalogs();
    }
  }, [isEditing]);

  useEffect(() => {
    if (crmConnection?.simi?.medioSources) {
      const existing: Record<string, number> = {};
      for (const entry of crmConnection.simi.medioSources) {
        existing[entry.source] = entry.medioId;
      }
      setMedioSources(existing);
    }
    setSelectedAsesorId(crmConnection?.simi?.asesorId);
  }, [crmConnection?.simi?.medioSources, crmConnection?.simi?.asesorId]);

  const handleMedioChange = (portalKey: string, value: string) => {
    setMedioSources((prev) => {
      if (value === "") {
        const next = { ...prev };
        delete next[portalKey];
        return next;
      }
      return { ...prev, [portalKey]: Number(value) };
    });
  };

  const handleSave = () => {
    if (!merchantId || !crmConnection?.id) return;

    if (!medioSources["default"]) {
      toast.error("Error", {
        description: "El medio 'Default' es obligatorio.",
      });
      return;
    }

    if (!selectedAsesorId) {
      toast.error("Error", {
        description: "El asesor es obligatorio.",
      });
      return;
    }

    const medioSourcesArray = Object.entries(medioSources).map(([source, medioId]) => ({
      source,
      medioId,
    }));

    setIsSaving(true);
    updateSimiConnection.mutate({
      merchantId,
      id: crmConnection.id,
      medioSources: medioSourcesArray,
      asesorId: selectedAsesorId,
    });
  };

  const hasConfiguration = () => {
    const hasMedioDefault = crmConnection?.simi?.medioSources?.some(
      (ms: any) => ms.source === "default"
    );
    return !!(hasMedioDefault && crmConnection?.simi?.asesorId);
  };

  const getCatalogLabel = (
    items: SimiCatalogItem[] | undefined,
    id: number | undefined
  ): string => {
    if (!id || !items) return "";
    const item = items.find((i) => i.id === id);
    return item?.value || "";
  };

  if (crmConnection?.provider !== "simi") {
    return <></>;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-bold mb-2">Variables Globales</p>
            <p className="text-sm text-gray-600">
              Configura los medios para cada portal. El medio "Default" es obligatorio.
            </p>
          </div>
          <div className="flex flex-row gap-2">
            {hasConfiguration() ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Configurado
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                Sin configurar
              </span>
            )}
            {!isEditing && (
              <Button
                variant="main"
                size="sm"
                onClick={() => {
                  setIsEditing(true);
                  if (!catalogs) {
                    fetchCatalogs();
                  }
                }}
              >
                Editar
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <>
            {isLoadingCatalogs ? (
              <div className="flex flex-row gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500">Cargando catalogos de Simi...</p>
              </div>
            ) : !catalogs ? (
              <p className="text-sm text-gray-500">No se pudieron cargar los catalogos de Simi.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {portalEntries.map((portal) => {
                  const currentValue = medioSources[portal.key];
                  return (
                    <div key={portal.key}>
                      <Label className="text-sm font-semibold text-gray-700">
                        Medio - {portal.label}
                        {portal.required && <span className="text-red-500"> *</span>}
                      </Label>
                      <select
                        value={currentValue !== undefined ? String(currentValue) : ""}
                        onChange={(e) => handleMedioChange(portal.key, e.target.value)}
                        className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="">{`Selecciona un medio para ${portal.label}`}</option>
                        {catalogs.medio.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}

                <div>
                  <Label className="text-sm font-semibold text-gray-700">
                    Asesor <span className="text-red-500">*</span>
                  </Label>
                  <select
                    value={selectedAsesorId !== undefined ? String(selectedAsesorId) : ""}
                    onChange={(e) =>
                      setSelectedAsesorId(
                        e.target.value === "" ? undefined : Number(e.target.value)
                      )
                    }
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Selecciona un asesor</option>
                    {catalogs.asesores.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4 flex flex-row gap-2">
                  <Button
                    variant="main"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || !medioSources["default"] || !selectedAsesorId}
                  >
                    {isSaving && <Loader2 className="size-4 animate-spin" />}
                    Guardar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      if (crmConnection?.simi?.medioSources) {
                        const existing: Record<string, number> = {};
                        for (const entry of crmConnection.simi.medioSources) {
                          existing[entry.source] = entry.medioId;
                        }
                        setMedioSources(existing);
                      } else {
                        setMedioSources({});
                      }
                      setSelectedAsesorId(crmConnection?.simi?.asesorId);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {hasConfiguration() ? (
              <>
                {portalEntries.map((portal) => {
                  const entry = (crmConnection?.simi?.medioSources || []).find(
                    (ms: any) => ms.source === portal.key
                  );
                  return (
                    <div key={portal.key} className="flex items-center justify-between text-xs">
                      <p className="font-medium text-gray-600">Medio - {portal.label}:</p>
                      {entry ? (
                        <p className="text-gray-700">
                          {getCatalogLabel(catalogs?.medio, entry.medioId) ||
                            `ID: ${entry.medioId}`}
                        </p>
                      ) : (
                        <p className="text-gray-400 italic">No configurado</p>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between text-xs">
                  <p className="font-medium text-gray-600">Asesor:</p>
                  <p className="text-gray-700">
                    {getCatalogLabel(catalogs?.asesores, crmConnection?.simi?.asesorId) ||
                      `ID: ${crmConnection?.simi?.asesorId}`}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500">
                Haz clic en Editar para configurar los valores de creacion de leads.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimiGlobalVariablesConfiguration;
